// ./src/components/features/Forms/FormFieldsEditModal/CoordinatesFieldOptionsEditor.tsx
import React, { useState } from "react";
import {
  Stack,
  Select,
  NumberInput,
  MultiSelect,
  Text,
  Group,
  Paper,
  Switch,
  Badge,
} from "@mantine/core";
import { AppButton, PolygonMapEditor } from "@/components/generic";
import { CoordinatesFieldOptions } from "./fieldOptionsTypes";

export interface CoordinatesFieldOptionsEditorProps {
  fieldName?: string;
  value: CoordinatesFieldOptions;
  onChange: (options: CoordinatesFieldOptions) => void;
}

const FORMAT_OPTIONS = [
  { value: "decimal", label: "Decimal degrees (45.4215, -75.6972)" },
  { value: "dms", label: "Degrees-minutes-seconds (45°25'17\"N, 75°41'50\"W)" },
];

const REGION_OPTIONS = [
  { value: "north_america", label: "North America" },
  { value: "south_america", label: "South America" },
  { value: "europe", label: "Europe" },
  { value: "africa", label: "Africa" },
  { value: "asia", label: "Asia" },
  { value: "oceania", label: "Oceania" },
  { value: "antarctica", label: "Antarctica" },
];

const PRECISION_OPTIONS = [
  { value: "0", label: "0 (111 km)" },
  { value: "1", label: "1 (11.1 km)" },
  { value: "2", label: "2 (1.1 km)" },
  { value: "3", label: "3 (110 m)" },
  { value: "4", label: "4 (11 m)" },
  { value: "5", label: "5 (1.1 m)" },
  { value: "6", label: "6 (0.11 m) - default" },
];

export const CoordinatesFieldOptionsEditor: React.FC<
  CoordinatesFieldOptionsEditorProps
> = ({ fieldName, value, onChange }) => {
  const [polygonEditorOpen, setPolygonEditorOpen] = useState(false);

  const handleChange = (
    field: keyof CoordinatesFieldOptions,
    fieldValue: any
  ) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const handleRegionsChange = (regions: string[]) => {
    onChange({
      ...value,
      allowedRegions: regions.length > 0 ? regions : undefined,
    });
  };

  const handleBoundingBoxToggle = (enabled: boolean) => {
    if (enabled) {
      onChange({
        ...value,
        boundingBox: {
          north: 90,
          south: -90,
          east: 180,
          west: -180,
        },
      });
    } else {
      const { boundingBox, ...rest } = value;
      onChange(rest);
    }
  };

  const handleBoundingBoxChange = (
    field: "north" | "south" | "east" | "west",
    fieldValue: number | string
  ) => {
    if (!value.boundingBox) return;

    const numValue =
      typeof fieldValue === "string" ? parseFloat(fieldValue) : fieldValue;

    onChange({
      ...value,
      boundingBox: {
        ...value.boundingBox,
        [field]: isNaN(numValue) ? 0 : numValue,
      },
    });
  };

  const handleRadiusToggle = (enabled: boolean) => {
    if (enabled) {
      onChange({
        ...value,
        radiusCenter: { lat: 0, lng: 0 },
        radiusKm: 100,
      });
    } else {
      const { radiusCenter, radiusKm, ...rest } = value;
      onChange(rest);
    }
  };

  const handleRadiusCenterChange = (
    field: "lat" | "lng",
    fieldValue: number | string
  ) => {
    if (!value.radiusCenter) return;

    const numValue =
      typeof fieldValue === "string" ? parseFloat(fieldValue) : fieldValue;

    onChange({
      ...value,
      radiusCenter: {
        ...value.radiusCenter,
        [field]: isNaN(numValue) ? 0 : numValue,
      },
    });
  };

  const handlePolygonsChange = (
    polygons:
      | Array<{
          title: string;
          points: Array<{ lat: number; lng: number }>;
        }>
      | undefined
  ) => {
    onChange({
      ...value,
      polygons,
    });
  };

  const hasBoundingBox = !!value.boundingBox;
  const hasRadius = !!value.radiusCenter && value.radiusKm != null;
  const hasRegions = value.allowedRegions && value.allowedRegions.length > 0;
  const hasPolygons = value.polygons && value.polygons.length > 0;
  const polygonCount = value.polygons?.length || 0;
  const totalPoints =
    value.polygons?.reduce((sum, p) => sum + (p.points?.length ?? 0), 0) || 0;

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure coordinate format and geographic restrictions.
      </Text>

      {/* Format Selection */}
      <Select
        label="Coordinate format"
        description="How users should enter coordinates"
        data={FORMAT_OPTIONS}
        value={value.format ?? "decimal"}
        onChange={(val) =>
          handleChange("format", val === "decimal" ? undefined : val)
        }
        size="sm"
      />

      {/* Decimal Precision */}
      <Select
        label="Output precision"
        description="Decimal places for stored coordinates"
        data={PRECISION_OPTIONS}
        value={String(value.decimalPrecision ?? 6)}
        onChange={(val) =>
          handleChange(
            "decimalPrecision",
            val === "6" ? undefined : parseInt(val || "6", 10)
          )
        }
        size="sm"
      />

      {/* Region Restriction */}
      <MultiSelect
        label="Allowed regions"
        description="Restrict coordinates to specific continents"
        placeholder="All regions allowed"
        data={REGION_OPTIONS}
        value={value.allowedRegions ?? []}
        onChange={handleRegionsChange}
        clearable
        size="sm"
      />

      {/* Bounding Box Restriction */}
      <Switch
        label="Bounding box restriction"
        description="Limit coordinates to a rectangular area"
        checked={hasBoundingBox}
        onChange={(e) => handleBoundingBoxToggle(e.currentTarget.checked)}
        disabled={hasRadius}
        size="sm"
      />

      {hasBoundingBox && value.boundingBox && (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Text size="xs" fw={500}>
              Bounding Box Coordinates
            </Text>
            <NumberInput
              label="North (max latitude)"
              value={value.boundingBox.north}
              onChange={(val) => handleBoundingBoxChange("north", val || 0)}
              min={-90}
              max={90}
              decimalScale={6}
              size="sm"
            />
            <Group grow>
              <NumberInput
                label="West (min longitude)"
                value={value.boundingBox.west}
                onChange={(val) => handleBoundingBoxChange("west", val || 0)}
                min={-180}
                max={180}
                decimalScale={6}
                size="sm"
              />
              <NumberInput
                label="East (max longitude)"
                value={value.boundingBox.east}
                onChange={(val) => handleBoundingBoxChange("east", val || 0)}
                min={-180}
                max={180}
                decimalScale={6}
                size="sm"
              />
            </Group>
            <NumberInput
              label="South (min latitude)"
              value={value.boundingBox.south}
              onChange={(val) => handleBoundingBoxChange("south", val || 0)}
              min={-90}
              max={90}
              decimalScale={6}
              size="sm"
            />
          </Stack>
        </Paper>
      )}

      {/* Radius Restriction */}
      <Switch
        label="Radius restriction"
        description="Limit coordinates to a circular area"
        checked={hasRadius}
        onChange={(e) => handleRadiusToggle(e.currentTarget.checked)}
        disabled={hasBoundingBox}
        size="sm"
      />

      {hasRadius && value.radiusCenter && (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Text size="xs" fw={500}>
              Center Point & Radius
            </Text>
            <Group grow>
              <NumberInput
                label="Center latitude"
                value={value.radiusCenter.lat}
                onChange={(val) => handleRadiusCenterChange("lat", val || 0)}
                min={-90}
                max={90}
                decimalScale={6}
                size="sm"
              />
              <NumberInput
                label="Center longitude"
                value={value.radiusCenter.lng}
                onChange={(val) => handleRadiusCenterChange("lng", val || 0)}
                min={-180}
                max={180}
                decimalScale={6}
                size="sm"
              />
            </Group>
            <NumberInput
              label="Radius (km)"
              value={value.radiusKm}
              onChange={(val) => handleChange("radiusKm", val || 1)}
              min={0.1}
              decimalScale={2}
              size="sm"
            />
          </Stack>
        </Paper>
      )}

      {/* Polygon Restrictions */}
      <Paper p="sm" withBorder>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="xs" fw={500}>
              Custom Regions (Polygons)
            </Text>
            {hasPolygons && (
              <Badge size="xs" variant="light">
                {polygonCount} region{polygonCount !== 1 ? "s" : ""} (
                {totalPoints} points)
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            Draw custom geographic areas on a map. Coordinates must fall within
            at least one of the defined regions.
          </Text>
          <AppButton
            size="xs"
            variant={hasPolygons ? "secondary" : "primary"}
            iconName="Map"
            onClick={() => setPolygonEditorOpen(true)}
          >
            {hasPolygons ? "Edit Regions" : "Define Regions"}
          </AppButton>
        </Stack>
      </Paper>

      {/* Polygon Editor Modal */}
      <PolygonMapEditor
        opened={polygonEditorOpen}
        onClose={() => setPolygonEditorOpen(false)}
        value={value.polygons}
        onChange={handlePolygonsChange}
        title={fieldName ? `Edit polygons for the '${fieldName}'` : undefined}
        maxPoints={100}
        maxPointsPerPolygon={50}
        maxPolygons={5}
      />

      {/* Help text about restrictions */}
      {!hasRegions && !hasBoundingBox && !hasRadius && !hasPolygons && (
        <Text size="xs" c="dimmed">
          No geographic restrictions. All valid coordinates will be accepted.
        </Text>
      )}

      {(hasRegions || hasBoundingBox || hasRadius || hasPolygons) && (
        <Text size="xs" c="dimmed">
          Coordinates must satisfy all enabled restrictions.
        </Text>
      )}
    </Stack>
  );
};
